import type {
  AuthenticatedAdmin,
  PlayerOverview,
  PlayerSummary,
} from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { ActivityTimeline } from "../components/ActivityTimeline.js";
import { ConfirmAction } from "../components/ConfirmAction.js";
import { GameHistory } from "../components/GameHistory.js";
import { KycReview } from "../components/KycReview.js";
import { LedgerList } from "../components/LedgerList.js";
import { PlayerCases } from "../components/PlayerCases.js";
import { ResponsibleGaming } from "../components/ResponsibleGaming.js";
import { SessionList } from "../components/SessionList.js";
import { asAmount, asDay, asMoment } from "../format.js";
import { rememberRecent } from "../operator-memory.js";

export type PlayerTab =
  | "activity"
  | "cases"
  | "games"
  | "history"
  | "kyc"
  | "overview"
  | "responsible-gaming"
  | "sessions"
  | "wallet";

const PLAYER_TABS: readonly PlayerTab[] = [
  "activity",
  "cases",
  "games",
  "history",
  "kyc",
  "overview",
  "responsible-gaming",
  "sessions",
  "wallet",
];

function canViewPlayerTab(role: AuthenticatedAdmin["role"], tab: PlayerTab) {
  switch (tab) {
    case "activity":
      return can(role, "audit.read");
    case "cases":
      return can(role, "cases.read");
    case "games":
      return can(role, "games.read");
    case "kyc":
      return can(role, "kyc.review");
    case "responsible-gaming":
      return can(role, "rg.read");
    case "sessions":
      return can(role, "players.sessions.read");
    case "wallet":
      return can(role, "ledger.read");
    default:
      return true;
  }
}

function readPlayerTab(role: AuthenticatedAdmin["role"]) {
  const value = new URLSearchParams(window.location.search).get("tab");
  const tab = PLAYER_TABS.includes(value as PlayerTab) ? (value as PlayerTab) : "overview";
  return canViewPlayerTab(role, tab) ? tab : "overview";
}

function writePlayerUrl(playerId: string | null, tab: PlayerTab, push = false) {
  const url = new URL(window.location.href);
  url.searchParams.set("screen", "players");
  if (playerId) {
    url.searchParams.set("player", playerId);
    url.searchParams.set("tab", tab);
  } else {
    url.searchParams.delete("player");
    url.searchParams.delete("tab");
  }
  window.history[push ? "pushState" : "replaceState"](null, "", url);
}

function writePlayerSearch(term: string) {
  const url = new URL(window.location.href);
  if (term.trim()) url.searchParams.set("q", term.trim());
  else url.searchParams.delete("q");
  url.searchParams.delete("player");
  url.searchParams.delete("tab");
  window.history.replaceState(null, "", url);
}

const initial = (player: { displayName: string | null; email: string }) =>
  (player.displayName ?? player.email).charAt(0).toUpperCase();

/** The status colour lives in the dot, so the word itself stays readable. */
function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`badge badge-${status}`}>
      <span className="dot" />
      {status}
    </span>
  );
}

/**
 * Search, then the 360 view of whoever was picked.
 *
 * Search is by prefix and not substring — the API enforces that, and it is why an operator cannot
 * ask "which of our players use gmail". The panel does not soften it into something friendlier.
 */
export function Players({ admin }: { admin: AuthenticatedAdmin }) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [player, setPlayer] = useState<PlayerOverview | null>(null);
  const [results, setResults] = useState<PlayerSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [tab, setTab] = useState<PlayerTab>(() => readPlayerTab(admin.role));
  const playerGeneration = useRef(0);
  const searchGeneration = useRef(0);
  const initialTerm = useRef(new URLSearchParams(window.location.search).get("q") ?? "").current;
  const [term, setTerm] = useState(initialTerm);

  useEffect(() => {
    let controller: AbortController | null = null;

    const restorePlayer = () => {
      controller?.abort();
      const requestGeneration = ++playerGeneration.current;
      const params = new URLSearchParams(window.location.search);
      const playerId = params.get("player");
      const nextTab = readPlayerTab(admin.role);
      setTab(nextTab);
      if (!playerId) {
        setPlayer(null);
        return;
      }
      if (params.get("tab") !== nextTab) writePlayerUrl(playerId, nextTab);

      const requestController = new AbortController();
      controller = requestController;
      api
        .player(playerId, requestController.signal)
        .then(({ player: found }) => {
          if (playerGeneration.current === requestGeneration) setPlayer(found);
        })
        .catch((caught: unknown) => {
          if (!requestController.signal.aborted && playerGeneration.current === requestGeneration)
            setError(caught instanceof ApiError ? caught.message : "Could not open that player.");
        });
    };

    restorePlayer();
    window.addEventListener("popstate", restorePlayer);
    return () => {
      controller?.abort();
      window.removeEventListener("popstate", restorePlayer);
    };
  }, [admin.role]);

  useEffect(() => {
    const controller = new AbortController();
    const requestGeneration = ++searchGeneration.current;
    setPending(true);
    api
      .searchPlayers(initialTerm, { signal: controller.signal })
      .then(({ nextCursor: next, players }) => {
        if (searchGeneration.current !== requestGeneration) return;
        setResults(players);
        setNextCursor(next);
        setSearched(Boolean(initialTerm));
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted && searchGeneration.current === requestGeneration)
          setError(caught instanceof ApiError ? caught.message : "Could not load players.");
      })
      .finally(() => {
        if (!controller.signal.aborted && searchGeneration.current === requestGeneration)
          setPending(false);
      });
    return () => controller.abort();
  }, [initialTerm]);

  async function search(event: FormEvent) {
    event.preventDefault();
    const requestGeneration = ++searchGeneration.current;
    setError(null);
    setPending(true);
    playerGeneration.current += 1;
    setPlayer(null);
    setTab("overview");
    writePlayerUrl(null, "overview");
    writePlayerSearch(term);

    try {
      const { nextCursor: next, players } = await api.searchPlayers(term);
      if (searchGeneration.current !== requestGeneration) return;
      setResults(players);
      setNextCursor(next);
      setSearched(true);
    } catch (caught) {
      if (searchGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Search failed.");
    } finally {
      if (searchGeneration.current === requestGeneration) setPending(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    const requestGeneration = ++searchGeneration.current;
    setPending(true);
    setError(null);
    try {
      const pageData = await api.searchPlayers(term, { before: nextCursor });
      if (searchGeneration.current !== requestGeneration) return;
      setResults((current) => [...current, ...pageData.players]);
      setNextCursor(pageData.nextCursor);
    } catch (caught) {
      if (searchGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load more players.");
    } finally {
      if (searchGeneration.current === requestGeneration) setPending(false);
    }
  }

  async function open(playerId: string, nextTab: PlayerTab = "overview", updateUrl = true) {
    const requestGeneration = ++playerGeneration.current;
    setError(null);
    try {
      const { player: found } = await api.player(playerId);
      if (playerGeneration.current !== requestGeneration) return;
      setPlayer(found);
      setTab(nextTab);
      if (updateUrl) writePlayerUrl(playerId, nextTab, true);
      rememberRecent(admin.id, {
        id: `player:${playerId}`,
        label: `Player ${playerId.slice(0, 8)}`,
        search: `?screen=players&player=${encodeURIComponent(playerId)}&tab=${nextTab}`,
      });
    } catch (caught) {
      if (playerGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not open that player.");
    }
  }

  function closePlayer() {
    playerGeneration.current += 1;
    setPlayer(null);
    writePlayerUrl(null, "overview");
  }

  if (player) {
    return (
      <section
        aria-label={`Player details for ${player.displayName ?? player.email}`}
        className="body player-page"
      >
        <div className="player-page-nav">
          <button className="btn btn-quiet btn-sm" onClick={closePlayer} type="button">
            ← Back to players
          </button>
        </div>
        <PlayerDetail
          admin={admin}
          key={player.id}
          onChanged={() => open(player.id, tab, false)}
          onTabChange={(next) => {
            setTab(next);
            writePlayerUrl(player.id, next, true);
          }}
          player={player}
          selectedTab={tab}
        />
      </section>
    );
  }

  return (
    <section className="body">
      <form className="search-row" onSubmit={search}>
        <div className="field-wrap">
          <svg
            aria-hidden="true"
            className="search-icon"
            fill="none"
            height="14"
            viewBox="0 0 16 16"
            width="14"
          >
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path
              d="M10.5 10.5L14 14"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
          <input
            aria-label="Find a player"
            className="input input-search"
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Email, display name or id"
            value={term}
          />
        </div>
        <button className="btn" disabled={pending} type="submit">
          {pending ? "Searching…" : "Search"}
        </button>
      </form>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      {/**
       * What the search came to, announced rather than only drawn.
       *
       * A sighted operator watches the table appear; a screen reader is told nothing, because
       * nothing moved focus and no error fired. `role="status"` is the polite half of `aria-live`:
       * it waits for a pause rather than interrupting, which is right for a result somebody asked
       * for and is expecting.
       *
       * One region covering every outcome, so "no match" and "four results" arrive the same way
       * instead of one of them being silent.
       */}
      <p className="visually-hidden" role="status">
        {pending
          ? "Searching."
          : searched && !error
            ? results.length === 0
              ? "No account matches that search."
              : `${results.length} ${results.length === 1 ? "account" : "accounts"} found.`
            : ""}
      </p>

      {searched && results.length === 0 && !error ? (
        <div className="card">
          <p className="empty">No account matches that. Search is by prefix, not by substring.</p>
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="card">
          <table className="mobile-cards">
            <thead>
              <tr>
                <th style={{ width: "52%" }}>Player</th>
                <th style={{ width: "24%" }}>Status</th>
                <th style={{ width: "24%" }}>Registered</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr
                  aria-label={`Open details for ${row.displayName ?? row.email}`}
                  className="clickable-row"
                  key={row.id}
                  onClick={() => void open(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void open(row.id);
                    }
                  }}
                  tabIndex={0}
                >
                  <td data-label="Player">
                    <span className="cell-main">
                      <span className={row.displayName ? "label-14" : "label-14 faint"}>
                        {row.displayName ?? "No display name"}
                      </span>
                      <span className="label-13 faint">{row.email}</span>
                    </span>
                  </td>
                  <td data-label="Status">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="label-13 dim" data-label="Registered">
                    {asDay(row.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextCursor ? (
            <button
              className="btn btn-quiet load-more-button"
              disabled={pending}
              onClick={() => void loadMore()}
              type="button"
            >
              {pending ? "Loading…" : "Load more"}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** What the operator is about to do. */
type Intent =
  | { kind: "force-logout" }
  | { amount: string; kind: "top-up" }
  | { kind: "status"; status: "active" | "banned" | "suspended" };

/** Parse an operator-entered XTS amount without ever routing money through a float. */
function playMoneyMinorUnits(value: string): string | null {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const amount = BigInt(match[1] ?? "0") * 100n + BigInt((match[2] ?? "").padEnd(2, "0"));
  return amount > 0n && amount <= 100_000_000n ? amount.toString() : null;
}

/**
 * The wording of each confirmation, in the operator's terms rather than the API's.
 *
 * "Ban" and "suspend" both end live sessions and the dialog says so: an operator should not have to
 * remember which controls have side effects.
 */
const INTENTS = {
  active: {
    confirmLabel: "Reinstate",
    danger: false,
    prompt: "The account can sign in and play again.",
    title: "Reinstate this account",
  },
  banned: {
    confirmLabel: "Ban",
    danger: true,
    prompt: "The account is closed to play and every live session ends immediately.",
    title: "Ban this account",
  },
  "force-logout": {
    confirmLabel: "Force logout",
    danger: true,
    prompt: "Every live session ends. The player can sign in again straight away.",
    title: "End every session",
  },
  suspended: {
    confirmLabel: "Suspend",
    danger: true,
    prompt: "Play stops and every live session ends. The account itself stays open.",
    title: "Suspend this account",
  },
} as const;

export function PlayerDetail({
  admin,
  onChanged,
  onTabChange,
  player,
  selectedTab,
}: {
  admin: AuthenticatedAdmin;
  onChanged: () => void;
  onTabChange?: (tab: PlayerTab) => void;
  player: PlayerOverview;
  selectedTab?: PlayerTab;
}) {
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState<Intent | null>(null);
  const [pending, setPending] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpRequested, setTopUpRequested] = useState<string | null>(null);
  const [tab, setTab] = useState<PlayerTab>("overview");
  const activeTab = selectedTab ?? tab;
  const chooseTab = (next: PlayerTab) => (onTabChange ? onTabChange(next) : setTab(next));

  useEffect(() => {
    if (!activeTab) return;
    const selected = document.querySelector<HTMLElement>('.player-tab[aria-current="page"]');
    const tabs = selected?.parentElement;
    tabs?.scrollTo?.({
      left: Math.max(
        0,
        (selected?.offsetLeft ?? 0) - (tabs.clientWidth - (selected?.clientWidth ?? 0)) / 2,
      ),
    });
  }, [activeTab]);

  const mayChangeStatus = can(admin.role, "players.status.write");
  const mayReadAudit = can(admin.role, "audit.read");
  const mayReadCases = can(admin.role, "cases.read");
  const mayWriteCases = can(admin.role, "cases.write");
  const maySeeSessions = can(admin.role, "players.sessions.read");
  const mayReadLedger = can(admin.role, "ledger.read");
  const directTopUp = admin.role === "super_admin";
  const mayTopUp = directTopUp || can(admin.role, "wallet.adjust.initiate");
  const mayReadGames = can(admin.role, "games.read");
  const mayReviewKyc = can(admin.role, "kyc.review");
  const mayRevoke = can(admin.role, "players.sessions.revoke");
  const maySeeResponsibleGaming = can(admin.role, "rg.read");

  /**
   * Carry out whatever the dialog was opened for.
   *
   * The reason travels with both kinds of action even though only a status change records it as a
   * field: the audit log takes it either way, and an operator who has to justify a ban should have
   * to justify ending somebody's sessions too.
   */
  async function perform(reason: string) {
    if (!intent) return;
    setError(null);
    setPending(true);

    try {
      if (intent.kind === "force-logout") {
        await api.forceLogout(player.id, reason);
      } else if (intent.kind === "top-up") {
        const result = await api.raiseAction({
          actionType: "wallet.play_money_top_up",
          justification: reason,
          payload: { amount: intent.amount, currency: "XTS" },
          targetId: player.id,
        });
        setTopUpAmount("");
        setTopUpRequested(
          "status" in result
            ? `${asAmount({ amount: intent.amount, currency: "XTS" })} was added to the player's cash balance.`
            : `${asAmount({ amount: intent.amount, currency: "XTS" })} top-up is waiting for another operator's approval.`,
        );
        if ("status" in result) onChanged();
      } else {
        await api.setStatus(player.id, { reason, status: intent.status });
      }
      setIntent(null);
      if (intent.kind !== "top-up") onChanged();
    } catch (caught) {
      // Left open with the reason still in it: a refused action is usually one worth retrying, and
      // making somebody retype a sentence is a good way to get a shorter one.
      setError(caught instanceof ApiError ? caught.message : "That did not go through.");
    } finally {
      setPending(false);
    }
  }

  const wording = intent
    ? intent.kind === "top-up"
      ? {
          confirmLabel: directTopUp ? "Top up now" : "Request top-up",
          danger: false,
          prompt: directTopUp
            ? `${asAmount({ amount: intent.amount, currency: "XTS" })} will be added to the player's cash balance immediately.`
            : `${asAmount({ amount: intent.amount, currency: "XTS" })} will move to the player's cash balance only after another operator approves it.`,
          title: directTopUp ? "Top up play-money now" : "Request play-money top-up",
        }
      : INTENTS[intent.kind === "force-logout" ? "force-logout" : intent.status]
    : null;

  return (
    <article className="card">
      <div className="detail-head">
        <span className="avatar">{initial(player)}</span>
        <div style={{ flex: 1 }}>
          <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
            <span className={player.displayName ? "heading-20" : "heading-20 faint"}>
              {player.displayName ?? "No display name"}
            </span>
            <StatusBadge status={player.status} />
          </div>
          <div className="label-13 dim" style={{ marginTop: 2 }}>
            {player.email}
          </div>
        </div>
        <span className="mono-12 faint">{player.id}</span>
      </div>

      {/*
        Actions the role cannot perform are absent rather than disabled: a greyed-out button
        advertises a capability somebody does not have. The server refuses them either way.
      */}
      {mayRevoke || mayChangeStatus ? (
        <div className="actions">
          <span className="spacer" />
          {mayRevoke ? (
            <button
              className="btn btn-danger btn-sm"
              disabled={player.activeSessions === 0}
              onClick={() => setIntent({ kind: "force-logout" })}
              type="button"
            >
              Force logout
            </button>
          ) : null}
          {/* Reinstating is not destructive, so it does not wear the destructive colour. */}
          {mayChangeStatus && player.status !== "active" ? (
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => setIntent({ kind: "status", status: "active" })}
              type="button"
            >
              Reinstate
            </button>
          ) : null}
          {mayChangeStatus && player.status === "active" ? (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setIntent({ kind: "status", status: "suspended" })}
              type="button"
            >
              Suspend
            </button>
          ) : null}
          {mayChangeStatus && player.status !== "banned" ? (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setIntent({ kind: "status", status: "banned" })}
              type="button"
            >
              Ban
            </button>
          ) : null}
        </div>
      ) : null}

      <nav aria-label="Player details" className="player-tabs">
        <button
          aria-current={activeTab === "overview" ? "page" : undefined}
          className="player-tab"
          onClick={() => chooseTab("overview")}
          type="button"
        >
          Overview
        </button>
        {mayReadLedger ? (
          <button
            aria-current={activeTab === "wallet" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("wallet")}
            type="button"
          >
            Wallet
          </button>
        ) : null}
        {mayReadGames ? (
          <button
            aria-current={activeTab === "games" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("games")}
            type="button"
          >
            Games
          </button>
        ) : null}
        {mayReadCases ? (
          <button
            aria-current={activeTab === "cases" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("cases")}
            type="button"
          >
            Cases
            {player.openCaseCount > 0 ? (
              <span className="tab-count">{player.openCaseCount}</span>
            ) : null}
          </button>
        ) : null}
        {mayReviewKyc ? (
          <button
            aria-current={activeTab === "kyc" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("kyc")}
            type="button"
          >
            KYC
          </button>
        ) : null}
        {maySeeSessions ? (
          <button
            aria-current={activeTab === "sessions" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("sessions")}
            type="button"
          >
            Sessions
            {player.activeSessions > 0 ? (
              <span className="tab-count">{player.activeSessions}</span>
            ) : null}
          </button>
        ) : null}
        {maySeeResponsibleGaming ? (
          <button
            aria-current={activeTab === "responsible-gaming" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("responsible-gaming")}
            type="button"
          >
            Responsible Gaming
          </button>
        ) : null}
        <button
          aria-current={activeTab === "history" ? "page" : undefined}
          className="player-tab"
          onClick={() => chooseTab("history")}
          type="button"
        >
          History
        </button>
        {mayReadAudit ? (
          <button
            aria-current={activeTab === "activity" ? "page" : undefined}
            className="player-tab"
            onClick={() => chooseTab("activity")}
            type="button"
          >
            Activity
          </button>
        ) : null}
      </nav>

      {/* An error outside the dialog: a force-logout that failed has no dialog left to show it in. */}
      {error && !intent ? (
        <p className="banner" role="alert" style={{ margin: "0 16px 16px" }}>
          {error}
        </p>
      ) : null}

      <section className="player-panel">
        {activeTab === "overview" ? (
          <>
            <div className="card-bar">
              <span className="label-14-strong">Account</span>
            </div>
            <div className="facts">
              <div className="fact">
                <div className="fact-label">Email verified</div>
                <div className="fact-value">{player.emailVerifiedAt ? "Yes" : "No"}</div>
              </div>
              <div className="fact">
                <div className="fact-label">Second factor</div>
                <div className="fact-value">{player.mfaEnabled ? "On" : "Off"}</div>
              </div>
              <div className="fact">
                <div className="fact-label">Locale</div>
                <div className="fact-value">{player.locale?.toUpperCase() ?? "—"}</div>
              </div>
              <div className="fact">
                <div className="fact-label">Registered</div>
                <div className="fact-value">{asDay(player.createdAt)}</div>
              </div>
            </div>
            <div className="card-bar">
              <span className="label-14-strong">Access</span>
            </div>
            <div className="facts">
              <div className="fact">
                <div className="fact-label">Sign-in methods</div>
                <div className="fact-value">
                  {[
                    player.passwordEnabled ? "password" : null,
                    ...player.linkedProviders.map((item) => item.provider),
                  ]
                    .filter(Boolean)
                    .join(", ") || "None"}
                </div>
              </div>
              <div className="fact">
                <div className="fact-label">Live sessions</div>
                <div className="fact-value">{player.activeSessions}</div>
              </div>
              <div className="fact">
                <div className="fact-label">Last activity</div>
                <div className="fact-value">
                  {player.lastSeenAt ? asMoment(player.lastSeenAt) : "Never signed in"}
                </div>
              </div>
              <div className="fact">
                <div className="fact-label">Player ID</div>
                <div className="fact-value mono-12">{player.id}</div>
              </div>
            </div>
          </>
        ) : null}

        {activeTab === "responsible-gaming" && maySeeResponsibleGaming ? (
          <ResponsibleGaming playerId={player.id} />
        ) : null}

        {activeTab === "kyc" && mayReviewKyc ? <KycReview playerId={player.id} /> : null}

        {activeTab === "wallet" && mayReadLedger ? (
          <>
            {topUpRequested ? (
              <p className="banner banner-quiet" role="status">
                {topUpRequested}
              </p>
            ) : null}
            {mayTopUp ? (
              <div className="card-bar">
                <div>
                  <div className="label-14-strong">Play-money cash top-up</div>
                  <div className="label-13 faint">
                    {directTopUp
                      ? "XTS only · applied immediately"
                      : "XTS only · second-operator approval required"}
                  </div>
                </div>
                <label className="visually-hidden" htmlFor="play-money-top-up">
                  Top-up amount in XTS
                </label>
                <input
                  className="input"
                  id="play-money-top-up"
                  inputMode="decimal"
                  onChange={(event) => setTopUpAmount(event.target.value)}
                  placeholder="25.00 XTS"
                  style={{ maxWidth: 150 }}
                  value={topUpAmount}
                />
                <button
                  className="btn btn-sm"
                  disabled={playMoneyMinorUnits(topUpAmount) === null}
                  onClick={() => {
                    const amount = playMoneyMinorUnits(topUpAmount);
                    if (amount) setIntent({ amount, kind: "top-up" });
                  }}
                  type="button"
                >
                  {directTopUp ? "Top up now" : "Request top-up"}
                </button>
              </div>
            ) : null}
            {player.balances.length === 0 ? (
              <p className="empty">This player has never held a balance.</p>
            ) : (
              <div className="facts">
                {player.balances.map((held) => (
                  <div className="fact" key={`${held.balance.currency}:${held.accountType}`}>
                    <div className="fact-label">{held.accountType}</div>
                    <div className="fact-value mono-12">{asAmount(held.balance)}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="card-bar">
              <span className="label-14-strong">Ledger</span>
              <span className="label-13 faint">newest first, corrections included</span>
            </div>
            <LedgerList playerId={player.id} />
          </>
        ) : null}

        {activeTab === "games" && mayReadGames ? <GameHistory playerId={player.id} /> : null}

        {activeTab === "cases" && mayReadCases ? (
          <PlayerCases
            admin={admin}
            canWrite={mayWriteCases}
            onChanged={onChanged}
            playerId={player.id}
          />
        ) : null}

        {activeTab === "sessions" && maySeeSessions ? (
          <>
            <div className="card-bar">
              <span className="label-14-strong">Sessions</span>
              <span className="label-13 faint">revoked ones included</span>
            </div>
            <SessionList playerId={player.id} />
          </>
        ) : null}

        {activeTab === "history" ? (
          <>
            <div className="card-bar">
              <span className="label-14-strong">Status history</span>
              <span className="label-13 faint">
                {player.statusHistory.length === 1
                  ? "1 entry"
                  : `${player.statusHistory.length} entries`}
              </span>
            </div>
            {player.statusHistory.length === 0 ? (
              <p className="empty">Nothing has changed since registration.</p>
            ) : (
              <ol className="timeline" style={{ listStyle: "none", margin: 0 }}>
                {player.statusHistory.map((entry) => (
                  <li className="event" key={`${entry.createdAt}-${entry.toStatus}`}>
                    <span className="rail">
                      <span className={entry.toStatus === "active" ? "node" : "node node-warn"} />
                    </span>
                    <span className="event-body">
                      <span className="transition">
                        <span className="label-14 dim">{entry.fromStatus}</span>
                        <span className="arrow">→</span>
                        <span className="label-14-strong">{entry.toStatus}</span>
                      </span>
                      <span className="label-13">{entry.reason}</span>
                      <span className="label-13 faint">
                        {asMoment(entry.createdAt)}
                        {entry.changedBy ? "" : " · by the platform"}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </>
        ) : null}

        {activeTab === "activity" && mayReadAudit ? (
          <ActivityTimeline targetId={player.id} />
        ) : null}
      </section>

      {intent && wording ? (
        <ConfirmAction
          confirmLabel={wording.confirmLabel}
          danger={wording.danger}
          error={error}
          onCancel={() => {
            setIntent(null);
            setError(null);
          }}
          onConfirm={perform}
          pending={pending}
          {...(intent.kind === "top-up" ? { minimumReason: 1 } : {})}
          prompt={wording.prompt}
          title={wording.title}
        />
      ) : null}
    </article>
  );
}
